import { useDisclosure } from '@chakra-ui/react'
import { max, orderBy } from 'lodash-es'
import { DateTime } from 'luxon'
import { useState } from 'react'

import { isNotNull } from '@/utils/helpers'

import CourseQ from '@/queries/CourseQ'
import {
  SAT_BOOTCAMP_ONBOARDING_MAKEUP_TRAINING_SLUG,
  SAT_BOOTCAMP_ONBOARDING_TEMPLATE_SLUG,
  SAT_BOOTCAMP_ONBOARDING_TOPIC,
  SessionAttendeeRole,
} from '@/queries/constants'
import SATUserQ from '@/queries/SATUserQ'
import SeriesQ from '@/queries/SeriesQ'
import SeriesRegistrationQ from '@/queries/SeriesRegistrationQ'
import SeriesTemplateQ from '@/queries/SeriesTemplateQ'
import SessionAttendeeQ from '@/queries/SessionAttendeeQ'
import SessionQ from '@/queries/SessionQ'
import TopicQ from '@/queries/TopicQ'
import TrainingQ from '@/queries/TrainingQ'
import TrainingSubmissionQ from '@/queries/TrainingSubmissionQ'
import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { useServerNow } from '@/client/hooks'
import { SQL } from '@/client/query'
import { RPCRefresh } from '@/client/rpc'
import { SAT_BOOTCAMP_PROGRAM_SLUG } from '@/client/services/programs/constants'
import { SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS } from '@/client/services/satBootcamp/constants'
import { getPositionInSeries } from '@/client/services/session'
import * as url from '@/client/services/url'
import { getTutorOfferingPageUrl } from '@/client/services/url'
import {
  getUserLevelInWorld,
  isLevelAtLeastMinLevel,
  SAT_BOOTCAMP_WORLD_SLUG,
} from '@/client/services/worlds'

import CompleteLessonPrepModal from '@/components/SATBootcamp/CompleteLessonPrepModal'
import useSATBootcampTutorSurveyTodos from '@/components/SATBootcamp/hooks/todos/useSATBootcampTutorSurveyTodos'
import CompleteTaskModal from '@/components/Worlds/Todos/CompleteTaskModal'
import { Priority, type TodoItem } from '@/components/Worlds/Todos/types'

const MAX_LESSON_PREP_TODOS_PER_BOOTCAMP = 3

const REFRESHER_TRAINING_CUTOFF_DATE = DateTime.local(2025, 8, 11)
const REFRESHER_TRAINING_LINK = 'https://form.typeform.com/to/L0LL0NT1'

export default function useSATBootcampTutorTodos(): TodoItem[] {
  const user = useUser()
  const query = useQuery()
  const now = useServerNow()

  const [activeLessonPrepSessionId, setActiveLessonPrepSessionId] = useState<number | null>(null)
  const surveyTodos = useSATBootcampTutorSurveyTodos()

  const isTutor = user.satBootcampStates.find(state => state.type === 'tutor') != null

  const {
    isOpen: isRefresherTrainingModalOpen,
    onOpen: onOpenRefresherTrainingModal,
    onClose: onCloseRefresherTrainingModal,
  } = useDisclosure()

  if (!isTutor) {
    return []
  }

  const hasHostedHighlyEngagingBootcamp =
    query(UserQ.hasHostedHighlyEngagingSATBootcamp(user.id)) ?? false

  const satBootcampLevel = getUserLevelInWorld({ user, worldSlug: SAT_BOOTCAMP_WORLD_SLUG })
  const isSeniorTutorOrHigher =
    satBootcampLevel != null &&
    isLevelAtLeastMinLevel({ level: satBootcampLevel, minLevel: 'senior-tutor' })

  const latestOnboardingSessionScheduled = query.one(
    SessionQ.where({
      tutor: user.id,
      limit: 1,
      order: [[SessionQ.start_date, 'desc']],
      include: [
        {
          association: 'Topic',
          where: SQL`${TopicQ.name} = ${SAT_BOOTCAMP_ONBOARDING_TOPIC}`,
        },
      ],
    })
  )
  const latestOnboardingSessionScheduledDate =
    latestOnboardingSessionScheduled(SessionQ.start_date) == null
      ? null
      : DateTime.fromISO(`${latestOnboardingSessionScheduled(SessionQ.start_date)}`)
  const hasUpcomingOnboardingSession =
    latestOnboardingSessionScheduledDate != null && latestOnboardingSessionScheduledDate > now

  const bootcampsCreated = query
    .many(
      SeriesQ.where(
        { includeAllResearchGroups: true },
        SQL`${SeriesQ.closed_reason} IS NULL
        AND ${SeriesQ.isHost({ userId: user.id })}
        AND ${SeriesQ.deleted_at} IS NULL
        AND ${SeriesQ.hasAnyTemplateSlug(SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS)}`
      )
    )
    .map(series => ({
      startDate: DateTime.fromISO(`${series(SeriesQ.getComputedStartDate())}`),
      hasEnded: series(SeriesQ.isClosed()) ?? false,
      researchGroup: series(SeriesQ.getResearchGroup()),
    }))

  const upcomingBootcampsCreated = bootcampsCreated
    .filter(bootcamp => bootcamp.startDate > now)
    .sort((a, b) => a.startDate.toMillis() - b.startDate.toMillis())

  const earliestBootcampStartDate =
    upcomingBootcampsCreated.length > 0 ? upcomingBootcampsCreated[0].startDate : null

  const onboardingDeadline = earliestBootcampStartDate?.minus({ hours: 24 }) ?? null
  const onboardingMakeupDeadline = earliestBootcampStartDate ?? null

  const sessionsToCompleteLessonPrep = query
    .many(
      SeriesQ.where(
        {
          includeAllResearchGroups: true,
          include: [
            {
              association: 'Course',
              required: true,
            },
          ],
        },
        SQL`${SeriesQ.closed_reason} IS NULL
        AND ${SeriesQ.isHost({ userId: user.id })}
        AND ${SeriesQ.hasAnyTemplateSlug(SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS)}
        AND (SELECT ${SeriesQ.getComputedStartDate()}) < NOW() + INTERVAL '5 DAY'
        AND NOT ${SeriesQ.isClosed()}`
      )
    )
    .flatMap(series => {
      const upcomingSessions = series.many(
        SeriesQ.getUpcomingSessions({
          order: [[SessionQ.start_date, 'asc']],
          limit: MAX_LESSON_PREP_TODOS_PER_BOOTCAMP,
        })
      )
      const courseSlug = series(CourseQ.slug) ?? ''
      const researchGroup = series(SeriesQ.getResearchGroup())

      return upcomingSessions.map((session, index) => {
        const sessionId = session(SessionQ.id)
        const startDate = session(SessionQ.start_date)
        const hasLessonPrepResponse = session(SessionQ.hasSATBootcampLessonPrepResponse()) ?? false
        const sessionIndexInSeries = getPositionInSeries(session) ?? 0

        return {
          sessionId,
          hasLessonPrepResponse,
          startDate,
          courseSlug,
          sessionIndexInSeries,
          isNextUpcomingSession: index === 0,
          researchGroup,
        }
      })
    })
    .filter(session => !session.hasLessonPrepResponse)

  const level = getUserLevelInWorld({ user, worldSlug: SAT_BOOTCAMP_WORLD_SLUG })

  const isTutorInWorld =
    level == null ? false : isLevelAtLeastMinLevel({ level, minLevel: 'tutor' })

  const latestBootcampHostedStartDate = max(
    bootcampsCreated.filter(bootcamp => bootcamp.hasEnded).map(bootcamp => bootcamp.startDate)
  )
  const hasCompletedRefresherTraining =
    query<boolean>(
      SQL`EXISTS(SELECT 1 ${SATUserQ.where(
        {
          userId: user.id,
        },
        SQL`${SATUserQ.hasCompletedRefresherTraining()}`
      )})`
    ) ?? false

  const earliestRegisteredUpcomingOnboardingSession = query.one(
    SeriesRegistrationQ.where({
      userId: user.id,
      include: [
        {
          association: 'Series',
          required: true,
          include: [
            {
              association: 'SeriesTemplate',
              required: true,
              where: SQL`${SeriesTemplateQ.slug} = ${SAT_BOOTCAMP_ONBOARDING_TEMPLATE_SLUG}`,
            },
          ],
          where: SQL`(
            SELECT ${
              // If Session is ongoing, we still want to count the user
              // as being registered for an upcoming session
              SeriesQ.getComputedEndDate()
            }) > NOW()
          `,
        },
      ],
      order: [[SQL`(SELECT ${SeriesQ.getComputedStartDate()})`, 'asc']],
      limit: 1,
    })
  )
  const earliestRegisteredUpcomingOnboardingSessionSeriesId =
    earliestRegisteredUpcomingOnboardingSession(SeriesRegistrationQ.series_id)
  const isRegisteredUpcomingOnboardingSession =
    earliestRegisteredUpcomingOnboardingSessionSeriesId != null

  const attendedOnboardingSession =
    query<boolean>(
      SQL`EXISTS (SELECT 1
          ${SessionAttendeeQ.where({
            userId: user.id,
            roles: [SessionAttendeeRole.Learner],
            include: [
              {
                association: 'Session',
                required: true,
                include: [
                  {
                    association: 'Topic',
                    required: true,
                    where: SQL`${TopicQ.name} = ${SAT_BOOTCAMP_ONBOARDING_TOPIC}`,
                  },
                ],
              },
            ],
          })}
        )`
    ) ?? false

  const hostSATBootcampUrl = getTutorOfferingPageUrl({
    type: 'program',
    slug: SAT_BOOTCAMP_WORLD_SLUG,
  })

  const onboardingMakeupTrainingSubmission = query.one(
    TrainingSubmissionQ.where({
      userId: user.id,
      include: [
        {
          association: 'Training',
          required: true,
          where: SQL`${TrainingQ.slug} = ${SAT_BOOTCAMP_ONBOARDING_MAKEUP_TRAINING_SLUG}`,
        },
      ],
    })
  )
  const hasCompletedOnboardingMakeup =
    onboardingMakeupTrainingSubmission(TrainingSubmissionQ.submitted_at) != null

  const onboardingMakeupTraining = query.one(
    TrainingQ.where({
      slug: SAT_BOOTCAMP_ONBOARDING_MAKEUP_TRAINING_SLUG,
    })
  )
  const onboardingMakeupTrainingLink = onboardingMakeupTraining(TrainingQ.link_training) ?? ''
  const onboardingMakeupTrainingSlug = onboardingMakeupTraining(TrainingQ.slug) ?? ''

  const hostOnboardingSessionTodo: TodoItem | null =
    isSeniorTutorOrHigher || hasHostedHighlyEngagingBootcamp
      ? {
          worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
          title: 'Host an onboarding session',
          subtitle: 'To help tutors prepare for their SAT!',
          completed: hasUpcomingOnboardingSession,
          link: getTutorOfferingPageUrl({
            type: 'program',
            slug: SAT_BOOTCAMP_PROGRAM_SLUG,
          }),
          deadline: null,
          priority: Priority.High,
          emoji: '🤝',
          targetLevel: 'tutor',
        }
      : null

  const createBootcampTodo: TodoItem = {
    worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
    title: 'Publish your bootcamp!',
    subtitle: 'To help learners prepare for the SAT!',
    completed: upcomingBootcampsCreated.length > 0,
    link: hostSATBootcampUrl,
    deadline: null,
    priority: Priority.High,
    emoji: '📅',
    disabled: isTutorInWorld
      ? null
      : {
          tooltip: 'You must become a tutor in this subworld before publishing a bootcamp.',
        },
    targetLevel: 'tutor',
  }

  const registerOnboardingSessionTodo: TodoItem | null =
    upcomingBootcampsCreated.length > 0
      ? {
          worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
          title: 'Register for an onboarding session',
          subtitle: 'To prepare for your SAT bootcamp.',
          completed: attendedOnboardingSession || isRegisteredUpcomingOnboardingSession,
          link: url.getSATOnboardingSessionsUrl(),
          priority: Priority.High,
          emoji: '📖',
          deadline: onboardingDeadline,
          targetLevel: 'tutor',
          showOnSeriesPage: true,
        }
      : null

  const attendOnboardingSessionTodo: TodoItem | null =
    upcomingBootcampsCreated.length > 0 && isRegisteredUpcomingOnboardingSession
      ? {
          worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
          title: 'Attend an onboarding session',
          subtitle: 'To prepare for your SAT bootcamp',
          completed: attendedOnboardingSession,
          link: url.getSeriesUrl(earliestRegisteredUpcomingOnboardingSessionSeriesId ?? 0),
          priority: Priority.High,
          emoji: '📖',
          deadline: onboardingDeadline,
          targetLevel: 'tutor',
          showOnSeriesPage: true,
        }
      : null

  const completeOnboardingSessionMakeupTodo: TodoItem | null =
    upcomingBootcampsCreated.length > 0 &&
    onboardingDeadline != null &&
    onboardingDeadline < now &&
    !attendedOnboardingSession
      ? {
          worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
          title: 'Complete the onboarding session makeup training',
          subtitle: 'To prepare for your SAT bootcamp',
          completed: hasCompletedOnboardingMakeup,
          link: url.getPersonalizedTrainingUrl({
            userId: user.id,
            uuidTraining: user.uuidTraining,
            worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
            trainingLink: onboardingMakeupTrainingLink,
            trainingSlug: onboardingMakeupTrainingSlug,
          }),
          priority: Priority.High,
          emoji: '📖',
          deadline: onboardingMakeupDeadline,
          targetLevel: 'tutor',
          showOnSeriesPage: true,
        }
      : null

  const lessonPrepTodos: TodoItem[] = sessionsToCompleteLessonPrep.map(session => ({
    worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
    title: `Prepare for Session ${session.sessionIndexInSeries} of your bootcamp`,
    subtitle: 'To prepare for your upcoming session',
    completed: false,
    onClick: () => {
      setActiveLessonPrepSessionId(session.sessionId)
    },
    priority: Priority.High,
    emoji: '📖',
    deadline: session.startDate != null ? DateTime.fromISO(session.startDate) : null,
    targetLevel: 'tutor',
    disabled: session.isNextUpcomingSession
      ? null
      : {
          tooltip: 'You can complete this task once earlier sessions have passed.',
        },
    showOnSeriesPage: true,
    additionalComponent: () => (
      <CompleteLessonPrepModal
        sessionId={session.sessionId ?? 0}
        open={activeLessonPrepSessionId === session.sessionId}
        onClose={() => setActiveLessonPrepSessionId(null)}
        courseSlug={session.courseSlug}
        sessionIndexInSeries={session.sessionIndexInSeries}
      />
    ),
  }))

  const shouldSkipRefresherTraining =
    latestBootcampHostedStartDate == null ||
    latestBootcampHostedStartDate > REFRESHER_TRAINING_CUTOFF_DATE

  const refresherTrainingTodo: TodoItem | null =
    upcomingBootcampsCreated.length > 0 && !shouldSkipRefresherTraining
      ? {
          worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
          title: 'Complete the returning SAT tutor training',
          subtitle: 'To prepare for your upcoming bootcamp',
          completed: hasCompletedRefresherTraining,
          onClick: onOpenRefresherTrainingModal,
          priority: Priority.High,
          emoji: '📖',
          deadline: upcomingBootcampsCreated[0].startDate,
          targetLevel: 'tutor',
          showOnSeriesPage: true,
          additionalComponent: () => (
            <CompleteTaskModal
              title="Complete the returning tutor training"
              body={() => (
                <>
                  It&apos;s been a while since you&apos;ve hosted an SAT Bootcamp, and our
                  curriculum has changed. Please complete this training so that you are up to date!
                </>
              )}
              link={url.getPersonalizedTrainingUrl({
                userId: user.id,
                trainingLink: REFRESHER_TRAINING_LINK,
                trainingSlug: null,
                uuidTraining: user.uuidTraining,
                worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
              })}
              ctaLabel="Start Task"
              open={isRefresherTrainingModalOpen}
              onClose={onCloseRefresherTrainingModal}
              onMarkComplete={async () => {
                onCloseRefresherTrainingModal()
                await RPCRefresh.markSATBootcampTutorRefresherTrainingComplete({})
              }}
            />
          ),
        }
      : null

  const allTodos: (TodoItem | null)[] = [
    createBootcampTodo,
    registerOnboardingSessionTodo,
    attendOnboardingSessionTodo,
    refresherTrainingTodo,
    completeOnboardingSessionMakeupTodo,
    ...lessonPrepTodos,
    hostOnboardingSessionTodo,
    ...surveyTodos,
  ]

  return orderBy(
    allTodos.filter(isNotNull).filter(todo => todo.deadline == null || now < todo.deadline),
    todo => todo.deadline?.toMillis() ?? Number.POSITIVE_INFINITY
  )
}
